
import { forwardRef, ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { useClipboard } from '@/hooks/useClipboard';

export interface CopyButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  text: string;
  onCopySuccess?: () => void;
  onCopyError?: (error: string) => void;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  label?: string;
  copiedLabel?: string;
}

const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  (
    {
      className,
      text,
      onCopySuccess,
      onCopyError,
      variant = 'secondary',
      size = 'md',
      showLabel = true,
      label = 'Copy',
      copiedLabel = 'Copied!',
      disabled,
      ...props
    },
    ref
  ) => {
    const { copy, copied, error } = useClipboard();

    const handleClick = async () => {
      const success = await copy(text);
      if (success) {
        onCopySuccess?.();
      } else if (error) {
        onCopyError?.(error);
      }
    };

    const baseStyles =
      'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
      primary:
        'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 active:bg-primary-800',
      secondary:
        'bg-slate-100 text-slate-900 hover:bg-slate-200 focus:ring-slate-500 active:bg-slate-300 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600',
      outline:
        'border-2 border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-950',
      ghost:
        'text-slate-600 hover:bg-slate-100 focus:ring-slate-500 dark:text-slate-300 dark:hover:bg-slate-800',
    };

    const sizes = {
      sm: 'px-3 py-2 text-sm min-h-[36px] gap-1.5',
      md: 'px-4 py-2.5 text-base min-h-[44px] gap-2',
      lg: 'px-6 py-3 text-lg min-h-[52px] gap-2.5',
    };

    const iconSizes = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    return (
      <button
        ref={ref}
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          copied && 'bg-green-600 hover:bg-green-600 text-white',
          className
        )}
        onClick={handleClick}
        disabled={disabled}
        title={copied ? copiedLabel : label}
        {...props}
      >
        {copied ? (
          // Checkmark icon
          <svg
            className={iconSizes[size]}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          // Copy icon
          <svg
            className={iconSizes[size]}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        )}
        {showLabel && (
          <span>{copied ? copiedLabel : label}</span>
        )}
      </button>
    );
  }
);

CopyButton.displayName = 'CopyButton';

export { CopyButton };
